import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Send, Share, SquarePlus } from "lucide-react";
import api from "../lib/api";
import { disablePush, enablePush, getCurrentSubscription, needsIOSInstall, pushSupported } from "../lib/push";

const PREF_LABELS = [
  ["announcements", "Comunicados do mentor"],
  ["agenda", "Lembretes de eventos da Agenda"],
  ["journey", "Nova fase liberada na Jornada"],
  ["inactivity", "Lembrete quando eu ficar inativo"],
  ["health", "Alerta de Saúde da Operação crítica"],
];

export const IOSInstallHint = () => (
  <div data-testid="push-ios-hint" className="text-[11px] text-gray-300 bg-[#0F1218] border border-[#282D3D] rounded-lg p-3 space-y-1">
    <p className="font-bold text-white">No iPhone, instale o app primeiro:</p>
    <p className="flex items-center gap-1">1. No Safari, toque em <Share className="w-3.5 h-3.5 inline" /> Compartilhar</p>
    <p className="flex items-center gap-1">2. Escolha <SquarePlus className="w-3.5 h-3.5 inline" /> "Adicionar à Tela de Início"</p>
    <p>3. Abra o Modo Caverna pelo ícone e ative as notificações aqui.</p>
  </div>
);

export const PushSettingsCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  const iosInstall = needsIOSInstall();

  const load = useCallback(async () => {
    const [sub, status] = await Promise.all([
      getCurrentSubscription().catch(() => null),
      api.get("/api/push/status").then((r) => r.data).catch(() => null),
    ]);
    setEnabled(Boolean(sub) && typeof Notification !== "undefined" && Notification.permission === "granted");
    if (status) setPrefs(status.preferences);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        toast.success("Notificações desativadas neste aparelho.");
      } else {
        await enablePush();
        toast.success("Notificações ativadas!");
      }
      await load();
    } catch (err) {
      toast.error(err?.message === "permission_denied"
        ? "Permissão negada. Libere as notificações nas configurações do navegador."
        : "Não foi possível ativar as notificações neste aparelho.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/api/push/test");
      toast.success("Notificação de teste enviada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar teste.");
    }
  };

  const setPref = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const { data } = await api.put("/api/push/preferences", { [key]: value });
      setPrefs(data.preferences);
    } catch {
      toast.error("Não foi possível salvar a preferência.");
      load();
    }
  };

  return (
    <div data-testid="push-settings-card" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 mt-4 space-y-3">
      <div className="flex items-center gap-2 text-white font-extrabold">
        <Bell className="w-4 h-4 text-[#FF6B00]" /> Notificações no celular
      </div>
      {!supported && !iosInstall && (
        <p data-testid="push-unsupported" className="text-xs text-gray-400">Este navegador não suporta notificações push.</p>
      )}
      {iosInstall && <IOSInstallHint />}
      {supported && !iosInstall && (
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="push-toggle-btn" onClick={toggle} disabled={busy}
            className={`px-4 py-2 rounded-lg text-xs font-extrabold uppercase flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
              enabled ? "bg-[#1F2330] border border-[#3E4760] text-gray-200 hover:border-red-400" : "bg-[#FF6B00] hover:bg-[#FF8533] text-black"
            }`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : enabled ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
            {enabled ? "Desativar neste aparelho" : "Ativar notificações"}
          </button>
          {enabled && (
            <button data-testid="push-test-btn" onClick={sendTest}
              className="px-3 py-2 rounded-lg text-xs font-bold border border-[#3E4760] text-gray-200 hover:border-[#FF6B00] flex items-center gap-1.5 transition-colors">
              <Send className="w-3.5 h-3.5" /> Enviar teste
            </button>
          )}
          <span data-testid="push-status-label" className="text-[11px] text-gray-400">
            {enabled ? "Ativas neste aparelho" : "Desativadas neste aparelho"}
          </span>
        </div>
      )}
      {prefs && (
        <div className="pt-2 border-t border-[#282D3D] space-y-2">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">O que você quer receber</p>
          {PREF_LABELS.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3 text-xs text-gray-200">
              {label}
              <input type="checkbox" data-testid={`push-pref-${key}`} checked={Boolean(prefs[key])}
                onChange={(e) => setPref(key, e.target.checked)} className="w-4 h-4 accent-[#FF6B00]" />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
